import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2, FileCheck2 } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

/**
 * Rééditer le PDF d'une facture déjà émise, et dire pourquoi.
 *
 * Le contenu de la facture ne bouge pas — son numéro, son client, ses lignes,
 * ses montants, sa date. Seule la feuille sur laquelle elle s'imprime a
 * changé. Le PDF n'étant stocké nulle part, il se refabrique au
 * téléchargement et sort déjà avec le bon gabarit : ce qui manquait, c'est la
 * trace qui explique pourquoi un client peut détenir deux exemplaires qui ne
 * se ressemblent pas.
 *
 * La trace vit dans le journal d'audit, en écriture seule. Les motifs
 * viennent du serveur : deux listes finiraient par ne pas dire la même chose.
 */
export default function RepriseEditionDialog({ open, onOpenChange, invoice, onConsignee }) {
  const [motifs, setMotifs] = useState([]);
  const [motif, setMotif] = useState('');
  const [precision, setPrecision] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrecision('');
    (async () => {
      try {
        const { data } = await api.get('/invoices/reprise-edition/motifs');
        setMotifs(data || []);
        setMotif(data?.[0]?.code || '');
      } catch {
        // Sans la liste, on ne devine aucun motif : le champ reste vide et le
        // serveur refusera plutôt que d'enregistrer un motif inventé.
        setMotifs([]);
        setMotif('');
      }
    })();
  }, [open]);

  const precisionRequise = motif === 'AUTRE';
  const pretAEnvoyer = !!motif && (!precisionRequise || !!precision.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!pretAEnvoyer) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/reprise-edition`, {
        motif,
        precision: precision.trim() || undefined,
      });
      toast({
        title: 'Réédition consignée',
        description: `${invoice.id} — ${data.motif_libelle}. Le contenu de la facture n'a pas changé.`,
      });
      onConsignee?.(data);
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: 'Erreur', description: typeof d === 'string' ? d : 'Réédition impossible' });
    }
    setSubmitting(false);
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reprise-edition-dialog">
        <DialogHeader>
          <DialogTitle>Rééditer le PDF</DialogTitle>
          <DialogDescription>
            Facture <strong>{invoice.id}</strong> — {invoice.client}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="p-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] text-[#4A4B63] flex items-start gap-2">
            <FileCheck2 className="w-4 h-4 mt-0.5 shrink-0 text-[#14B8A6]" />
            <div>
              Le <strong>contenu</strong> de la facture ne change pas : numéro, client,
              lignes, montants et date restent ceux de l'émission. Seule la feuille sur
              laquelle elle s'imprime a été corrigée. La réédition est consignée au
              journal, avec son motif.
            </div>
          </div>

          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">
              Motif de la réédition
            </label>
            <select
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              data-testid="reprise-motif"
              className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
            >
              {motifs.length === 0 && <option value="">— liste indisponible —</option>}
              {motifs.map((m) => (
                <option key={m.code} value={m.code}>{m.libelle}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">
              Précision {precisionRequise ? '' : '(facultative)'}
            </label>
            <input
              value={precision}
              onChange={(e) => setPrecision(e.target.value)}
              data-testid="reprise-precision"
              placeholder={precisionRequise ? 'Dites ce qui a été corrigé' : 'Ex. mention de certification retirée du pied'}
              className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
            />
            {precisionRequise && !precision.trim() && (
              <p className="text-[11px] text-[#B45309] mt-1">
                « Autre » ne dit rien tout seul : c'est précisément ce qu'on viendra
                chercher dans trois ans.
              </p>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63]"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !pretAEnvoyer}
              data-testid="reprise-submit"
              className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />}
              Consigner et télécharger
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
